import { useId, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { DialogClose } from '@/components/ui/dialog'
import { EnumSelect } from '@/components/ui/enum-select'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { FormDialog, FormDialogContent } from '@/components/ui/form-dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { apiClient } from '@/lib/api/client'
import type { Customer } from '@/lib/api/types'
import { customerIdentityValue } from '@/lib/customer-identity'
import { quickEditErrorMessage } from '@/lib/quick-edit'
import {
  DUE_DATE_NOTICE,
  dueDateNoticeMessage,
  dueDateNoticeSubject,
  kampalaDay,
} from '@/lib/record-activity'
import { customerGreetingName } from '@/lib/customer-messages'

export interface DueDateNoticeRecord {
  kind: 'inquiry' | 'consignment'
  id: string
  customerId: string | null | undefined
  customer: Customer | null | undefined
  /** How the customer knows it: a shipment's reference and title, or what they asked for. */
  reference: string
}

type Channel = 'EMAIL' | 'WHATSAPP'

function channelsFor(customer: Customer | null | undefined) {
  const email = customer ? customerIdentityValue(customer, 'email') : ''
  const phone = customer ? customerIdentityValue(customer, 'phone').replace(/\D/g, '') : ''
  return { email, phone }
}

/**
 * Offered after an admin moves a due date: tell the customer, in a message
 * the admin can edit, through the same send path as the record's own
 * communications. Nothing goes out unless they press Send. Mount it only
 * while it is open, so each notice starts from the date it is about.
 */
export function DueDateNotice({
  record,
  previousDueAt,
  dueAt,
  open,
  onOpenChange,
  onSent,
}: {
  record: DueDateNoticeRecord
  previousDueAt: string | null
  dueAt: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSent?: () => void
}) {
  const id = useId()
  const { email, phone } = channelsFor(record.customer)
  const channels: Channel[] = [
    ...(email ? ['EMAIL' as const] : []),
    ...(phone ? ['WHATSAPP' as const] : []),
  ]
  // Never their email address: "Hello," when there is no name worth using.
  const customerName = customerGreetingName(record.customer)
  const [channel, setChannel] = useState<Channel>(channels[0] ?? 'EMAIL')
  const [reason, setReason] = useState('')
  const [subject, setSubject] = useState(() => dueDateNoticeSubject(record))
  const [message, setMessage] = useState(() =>
    dueDateNoticeMessage({ ...record, customerName, dueAt, reason: '' }),
  )
  // The message follows the reason until the admin writes it themselves.
  const [edited, setEdited] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function changeReason(next: string) {
    setReason(next)
    if (!edited) setMessage(dueDateNoticeMessage({ ...record, customerName, dueAt, reason: next }))
  }

  async function send(event: FormEvent) {
    event.preventDefault()
    if (!channels.length) return
    setSending(true)
    setError(null)
    const metadata = {
      purpose: DUE_DATE_NOTICE,
      due_at: dueAt,
      previous_due_at: previousDueAt,
      reason: reason.trim() || null,
    }
    const parent =
      record.kind === 'consignment' ? { consignment_id: record.id } : { inquiry_id: record.id }
    try {
      if (channel === 'EMAIL') {
        await apiClient.createCommunication({
          customer_id: record.customerId ?? null,
          ...parent,
          channel: 'EMAIL',
          template_key: 'HELM_STATUS_UPDATE',
          recipient: email,
          subject: subject.trim() || dueDateNoticeSubject(record),
          body: message.trim(),
          send: true,
          metadata,
        })
      } else {
        // As the record's WhatsApp contact does: logged, then opened in WhatsApp.
        await apiClient.createCommunication({
          customer_id: record.customerId ?? null,
          ...parent,
          channel: 'WHATSAPP',
          status: 'LOGGED',
          template_key: 'HELM_WHATSAPP_STATUS_UPDATE',
          recipient: `+${phone}`,
          body: message.trim(),
          metadata,
        })
        window.open(
          `https://wa.me/${phone}?text=${encodeURIComponent(message.trim())}`,
          '_blank',
          'noopener,noreferrer',
        )
      }
      onOpenChange(false)
      onSent?.()
    } catch (cause) {
      setError(quickEditErrorMessage(cause))
    } finally {
      setSending(false)
    }
  }

  return (
    <FormDialog open={open} onOpenChange={(next) => !sending && onOpenChange(next)}>
      <FormDialogContent
        title="Tell the customer?"
        description={`The due date is now ${kampalaDay(dueAt)}. Nothing is sent unless you press Send.`}
        submitLabel="Send"
        busyLabel="Sending"
        cancelLabel="Not now"
        busy={sending}
        error={error}
        errorTitle="Not sent"
        onSubmit={send}
        footer={
          channels.length ? undefined : (
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Close
              </Button>
            </DialogClose>
          )
        }
      >
        {channels.length ? (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={`${id}-reason`}>Reason</FieldLabel>
              <Input
                id={`${id}-reason`}
                value={reason}
                onChange={(event) => changeReason(event.target.value)}
                maxLength={200}
                placeholder="the supplier needs two more days"
                aria-describedby={`${id}-reason-help`}
              />
              <FieldDescription id={`${id}-reason-help`}>
                Short; it goes into the message below.
              </FieldDescription>
            </Field>
            {channels.length > 1 && (
              <Field>
                <FieldLabel htmlFor={`${id}-channel`}>Send by</FieldLabel>
                <EnumSelect
                  value={channel}
                  onValueChange={(value) => setChannel(value as Channel)}
                  options={channels.map((value) => ({
                    value,
                    label: value === 'EMAIL' ? `Email to ${email}` : `WhatsApp to +${phone}`,
                  }))}
                  triggerProps={{ id: `${id}-channel` }}
                />
              </Field>
            )}
            {channel === 'EMAIL' && (
              <Field>
                <FieldLabel htmlFor={`${id}-subject`}>Subject</FieldLabel>
                <Input
                  id={`${id}-subject`}
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                />
              </Field>
            )}
            <Field>
              <FieldLabel htmlFor={`${id}-message`}>Message</FieldLabel>
              <Textarea
                id={`${id}-message`}
                rows={7}
                value={message}
                onChange={(event) => {
                  setEdited(true)
                  setMessage(event.target.value)
                }}
              />
              {channels.length === 1 && (
                <FieldDescription>
                  {channel === 'EMAIL' ? `By email to ${email}.` : `By WhatsApp to +${phone}.`}
                </FieldDescription>
              )}
            </Field>
          </FieldGroup>
        ) : (
          <p className="text-sm text-muted-foreground">
            Helm has no confirmed email or phone for this customer, so there is nobody to tell from
            here.
          </p>
        )}
      </FormDialogContent>
    </FormDialog>
  )
}
